import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import React, { useState, useEffect } from "react";
import PageLoader from "@/components/PageLoader";

import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import DashboardLayout from "@/components/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import TransferPage from "@/pages/dashboard/TransferPage";
import BillsPage from "@/pages/dashboard/BillsPage";
import SavingsPage from "@/pages/dashboard/SavingsPage";
import LoansPage from "@/pages/dashboard/LoansPage";
import CooperativesPage from "@/pages/dashboard/CooperativesPage";
import CardsPage from "@/pages/dashboard/CardsPage";
import BudgetPage from "@/pages/dashboard/BudgetPage";
import TransactionsPage from "@/pages/dashboard/TransactionsPage";
import ProfilePage from "@/pages/dashboard/ProfilePage";

// Company Pages
import AboutPage from "@/pages/company/AboutPage";
import TeamPage from "@/pages/company/TeamPage";
import CareerPage from "@/pages/company/CareerPage";

// Cooperative Pages
import ServerCirclePage from "@/pages/cooperative/ServerCirclePage";
import CoopLoansPage from "@/pages/cooperative/CoopLoansPage";
import EduFuturePage from "@/pages/cooperative/EduFuturePage";

// Business Pages
import POSTerminalsPage from "@/pages/business/POSTerminalsPage";
import AgentNetworkPage from "@/pages/business/AgentNetworkPage";
import BusinessAccountPage from "@/pages/business/BusinessAccountPage";
import ATMCardsPage from "@/pages/business/ATMCardsPage";

// Legal Pages
import ComplaintsPage from "@/pages/legal/ComplaintsPage";
import LoanPrivacyPage from "@/pages/legal/LoanPrivacyPage";
import TermsPage from "@/pages/legal/TermsPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";

// Other Pages
import BuyBackPage from "@/pages/BuyBackPage";
import ContactPage from "@/pages/ContactPage";

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('britedge_auth') === 'true';
  return isAuthenticated ? children : <Navigate to="/auth" replace />;
};

// Placeholder component for pages under construction
const PlaceholderPage = ({ title }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">{title}</h1>
        <p className="text-slate-600 mb-8">This page is currently under construction</p>
        <button onClick={() => window.history.back()} className="bg-brand-blue text-white px-6 py-3 rounded-full">
          Go Back
        </button>
      </div>
    </div>
  );
};

// Loading wrapper component
function LoadingWrapper({ children }) {
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
    
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500); // Show loader for 500ms on route change

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      {loading && <PageLoader />}
      <div style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }}>
        {children}
      </div>
    </>
  );
}

function App() {
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    // Simulate initial app loading
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  if (initialLoading) {
    return <PageLoader />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <LoadingWrapper>
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          
          {/* Company Pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/career" element={<CareerPage />} />
          
          {/* Business Pages */}
          <Route path="/pos-terminals" element={<POSTerminalsPage />} />
          <Route path="/atm-cards" element={<ATMCardsPage />} />
          <Route path="/business-account" element={<BusinessAccountPage />} />
          <Route path="/agent-network" element={<AgentNetworkPage />} />
          
          {/* Cooperative Pages */}
          <Route path="/server-circle" element={<ServerCirclePage />} />
          <Route path="/coop-loans" element={<CoopLoansPage />} />
          <Route path="/edufuture" element={<EduFuturePage />} />
          
          {/* Buy-Back & Contact */}
          <Route path="/buyback" element={<BuyBackPage />} />
          <Route path="/contact" element={<ContactPage />} />
          
          {/* Legal Pages */}
          <Route path="/complaints" element={<ComplaintsPage />} />
          <Route path="/loan-privacy" element={<LoanPrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Dashboard */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardHome />} />
            <Route path="transfer" element={<TransferPage />} />
            <Route path="bills" element={<BillsPage />} />
            <Route path="savings" element={<SavingsPage />} />
            <Route path="loans" element={<LoansPage />} />
            <Route path="cooperatives" element={<CooperativesPage />} />
            <Route path="cards" element={<CardsPage />} />
            <Route path="budget" element={<BudgetPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>
        </Routes>
        </LoadingWrapper>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
